const moment = require("moment");
const LoadState = require("./util/LoadState");
const CR = require("./util/Cursor");
const AT = require("./ActionTypes");
const {getIn} = require("../../../shared/ImmHelpers");

// This file is a small wrapper around LoadState.js to further help deal with
// loading data. To the best of our knowledge, there is no disadvantage to
// using this file to manage loading a piece of data in new code.

// To use it, make a new request action in ActionTypes.js. Then add an entry
// to the table variable below that returns an object with
// - defaultValue: the data to use before anything is loaded. Can just be null.
// - keyList: where in the state tree the data and loading state is stored.
// - url: the endpoint to use for a loading request.
// - payload: the payload to send with a loading request.
// - action: the request action; its success and fail variants are used for
//   responses to the loading request.
// See the handlers of existing slot actions for examples of intended usage.

const table = {
	profile(userID) {
		if (!userID) return null;
		return {
			defaultValue: {data: null},
			keyList: ["profile", userID],
			payload: {userID},
			url: "/api/user/profile",
			action: AT.LOAD_PROFILE_SLOT_REQUEST,
		};
	},
	profileActivity(userID, timestamp) {
		if (!userID || !timestamp) return null;
		const m = moment(timestamp);
		const month = m.month();
		const year = m.year();
		const key = userID + "_" + year + "_" + month;
		return {
			defaultValue: {data: null},
			keyList: ["profileActivity", key],
			payload: {month, year, userID},
			url: "/api/user/profile/byMonth",
			action: AT.LOAD_PROFILE_ACTIVITY_SLOT_REQUEST,
		};
	},
	profileActivityDay(userID, timestamp) {
		if (!userID || !timestamp) return null;
		const m = moment(timestamp);
		const day = m.date();
		const month = m.month();
		const year = m.year();
		const key = userID + "_" + year + "_" + month + "_" + day;
		return {
			defaultValue: {data: null},
			keyList: ["profileActivityDay", key],
			payload: {month, year, day, userID},
			url: "/api/user/profile/byDay",
			action: AT.LOAD_PROFILE_ACTIVITY_DAY_SLOT_REQUEST,
		};
	},
	homework(userID) {
		if (!userID) return null;
		return {
			defaultValue: {data: null},
			keyList: ["homework", userID],
			payload: {userID, mustBeReleased: false},
			url: "/api/user/homework",
			action: AT.LOAD_HOMEWORK_SLOT_REQUEST,
		};
	},

	// School slots

	schoolEntities() {
		return {
			defaultValue: {},
			keyList: ["school", "entities"],
			payload: {},
			url: "/api/school/getAllEntities",
			action: AT.LOAD_SCHOOL_ENTITIES_SLOT_REQUEST,
		};
	},

	reportStudents(classID) {
		return {
			defaultValue: {},
			keyList: ["printStudents"],
			payload: {classID},
			url: "/api/school/getStudents",
			action: AT.LOAD_REPORT_STUDENTS_PRINT_SLOT_REQUEST,
		};
	},

	// Meant to be a single call made on a page for printing. No other links anywhere on this page.
	singleClass(classID) {
		return {
			defaultValue: {},
			keyList: ["school", "class"],
			payload: {classID},
			url: "/api/school/account/getSingleClass",
			action: AT.LOAD_SINGLE_CLASS_REQUEST,
		};
	},

	// Report slots

	reportResults({resultID, blockID, userID, isTrophy, space}) {
		let key = resultID || "b-" + blockID + "-" + userID;
		if (isTrophy) {
			key += "-trophy";
		} else if (space) {
			key += "-" + space;
		}
		const url = resultID
			? "/api/trainer/result/byID"
			: "/api/trainer/problemsByBlockID";
		return {
			defaultValue: {},
			keyList: ["report", "results", key],
			payload: {resultID, blockID, userID, isTrophy, space},
			url,
			action: AT.LOAD_REPORT_RESULT_SLOT_REQUEST,
		};
	},

	// Office slots
	parentData() {
		return {
			defaultValue: {},
			keyList: ["officeStudentData"],
			payload: {},
			url: "/api/user/parent/students",
			action: AT.LOAD_PARENT_DATA_SLOT_REQUEST,
		};
	},
};

// Internal helper
const atSlot = function(state, slot) {
	return CR(state)
		.at(slot.keyList)
		.withDefault(LoadState.loadInit(slot.defaultValue));
};

// loadFunc takes (slot) and starts the backend request. Typically you'd pass
// vault.api.loadSlot.
const loadStart = function(state, slot, loadFunc) {
	if (!slot) return state;
	return atSlot(state, slot)
		.canCreate()
		.set(value => LoadState.loadStart(value, () => loadFunc(slot)));
};

const loadRestart = function(state, slot) {
	if (!slot) return state;
	return atSlot(state, slot)
		.canCreate()
		.set(LoadState.loadRestart(slot.defaultValue));
};

const loadRestartMany = function(state, slotList) {
	slotList.forEach(slot => {
		state = loadRestart(state, slot);
	});
	return state;
};

const loadSuccess = function(state, slot, transform) {
	if (!slot) return state;
	return atSlot(state, slot).set(value => {
		value = LoadState.loadSuccess(value);
		if (typeof transform === "function") {
			value = transform(value);
		}
		return value;
	});
};

const loadError = function(state, slot, error) {
	if (!slot) return state;
	return atSlot(state, slot).set(value => {
		value = LoadState.loadError(value, error);
		return value;
	});
};

const getValue = function(state, slot) {
	if (!slot) return null;
	return getIn(state, slot.keyList) || LoadState.loadInit(slot.defaultValue);
};

module.exports = {
	table,
	loadStart,
	loadRestart,
	loadRestartMany,
	loadSuccess,
	loadError,
	getValue,
};
